import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import BookingModal from "../components/BookingModal";
import ClientConsent from "../components/ClientConsent";
import ClientMergeModal from "../components/ClientMergeModal";
import ClientModal from "../components/ClientModal";
import ClientNotes from "../components/ClientNotes";
import ClientTimeline from "../components/ClientTimeline";
import LoyaltyPanel from "../components/LoyaltyPanel";
import { tagPillClass } from "../components/clientTags";
import ConfirmDialog from "../components/ConfirmDialog";
import CustomerAvatar from "../components/CustomerAvatar";
import Toast from "../components/Toast";
import useUrlState, { param } from "../hooks/useUrlState";
import { apiErrorMessage } from "../services/api";
import { getCurrentUser } from "../services/authService";
import { fetchBranches } from "../services/branchService";
import {
  deleteClient,
  fetchClient,
  fetchClientTags,
  fetchDuplicates,
} from "../services/clientService";
import { formatDate } from "../services/datetime";
import { hasFeature } from "../services/features";
import { can } from "../services/permissions";

const TABS = ["overview", "timeline", "notes", "consent", "loyalty"];

/** The Loyalty tab needs loyalty.view and the plan's loyalty feature. */
const tabVisible = (name) =>
  name !== "loyalty" || (can(getCurrentUser(), "loyalty.view") && hasFeature(getCurrentUser(), "loyalty"));

/**
 * BIZ-16 — one client, everything known about them.
 *
 * Four tabs over one record: who they are, what they have booked, what staff
 * wrote down, and what they have agreed to. They live on one page rather than
 * four routes because reception moves between them constantly — the question
 * "when were they last in, and may we text them?" is one question.
 *
 * ## Booking from here
 *
 * The button hands the booking modal this client and the branch list. Nothing
 * asks who it is for again, and the modal loads whichever branch's providers
 * get picked. It is the same modal the diary uses, so a booking taken from a
 * profile goes through exactly the same engine and the same idempotency key.
 */
export default function ClientProfile() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [tags, setTags] = useState([]);
  const [branches, setBranches] = useState([]);
  const [duplicates, setDuplicates] = useState([]);
  // The open tab lives in the URL, so Back from a booking returns to it.
  const { values: tabState, set: setTabState } = useUrlState(
    { tab: param.oneOf(TABS, "overview") },
    { pageKey: null }
  );
  const { tab } = tabState;
  const setTab = useCallback((next) => setTabState({ tab: next }), [setTabState]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [editOpen, setEditOpen] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [toast, setToast] = useState(null);

  const user = getCurrentUser();
  const mayUpdate = can(user, "clients.update");
  const mayDelete = can(user, "clients.delete");
  const mayBook = can(user, "appointments.create");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setClient(await fetchClient(id));
    } catch (err) {
      setError(apiErrorMessage(err, t("clients.loadOneFailed")));
      setClient(null);
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load, i18n.language]);

  useEffect(() => {
    fetchClientTags()
      .then(setTags)
      .catch(() => setTags([]));

    fetchBranches(1, 100)
      .then(({ branches: list }) => setBranches(list))
      .catch(() => setBranches([]));
  }, [i18n.language]);

  // Offered rather than acted on: a possible duplicate is a prompt to look,
  // never a merge that happens by itself.
  useEffect(() => {
    if (!id) return;

    fetchDuplicates(id)
      .then(setDuplicates)
      .catch(() => setDuplicates([]));
  }, [id, client?.updated_at]);

  const confirmDelete = async () => {
    setDeleteError("");
    setDeleteBusy(true);
    try {
      await deleteClient(id);
      navigate("/clients");
    } catch (err) {
      setDeleteError(apiErrorMessage(err, t("clients.deleteFailed")));
    } finally {
      setDeleteBusy(false);
    }
  };

  if (loading) {
    return <p className="py-16 text-center text-sm text-ink-2">{t("common.loading")}</p>;
  }

  if (error || !client) {
    return (
      <div>
        <Link to="/clients" className="text-sm font-medium text-accent-text hover:underline">
          ← {t("clients.backToList")}
        </Link>
        <div className="mt-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
          {error || t("clients.notFound")}
        </div>
      </div>
    );
  }

  const money = () => {
    const symbol = client.lifetime_currency?.symbol ?? client.lifetime_currency?.code;
    const value = client.lifetime_value ?? "0";
    return symbol ? `${symbol} ${value}` : value;
  };

  return (
    <div>
      <Link to="/clients" className="text-sm font-medium text-accent-text hover:underline">
        ← {t("clients.backToList")}
      </Link>

      {/* Header */}
      <div className="card mt-3 mb-4 flex flex-wrap items-start justify-between gap-4 p-5">
        <div className="flex items-start gap-4">
          {/* The linked app account's own photo, else the initial. */}
          <CustomerAvatar name={client.name} photoUrl={client.photo_url} seed={client.id} size="lg" />
          <div>
          <h1 className="text-xl font-bold">{client.name}</h1>

          <div className="mt-1 space-y-1 text-sm text-ink-2">
            {client.phone_national && (
              <p dir="ltr">
                {client.phone_national}
                {/* The canonical form is what the system dials and dedupes on. */}
                {client.phone && client.phone !== client.phone_national && (
                  <span className="ms-2 text-xs text-ink-2">{client.phone}</span>
                )}
              </p>
            )}
            {client.email && (
              <p dir="ltr" className="text-ink-2">
                {client.email}
              </p>
            )}
            {client.reference && (
              <p className="text-xs text-ink-2">
                {t("clients.form.reference")}: {client.reference}
              </p>
            )}
          </div>

          <div className="mt-2 flex flex-wrap gap-2">
            {(client.tags ?? []).map((tag) => (
              <span key={tag.id} className={`${tagPillClass(tag)} px-3 py-1 text-xs`}>
                {tag.name}
              </span>
            ))}
            {client.is_registered && (
              <span className="rounded-full bg-accent-wash px-3 py-1 text-xs text-accent-text">
                {t("clients.registeredHint")}
              </span>
            )}
          </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {mayBook && (
            <button type="button" className="btn-primary" onClick={() => setBookingOpen(true)}>
              {t("clients.book")}
            </button>
          )}
          {mayUpdate && (
            <button type="button" className="btn-secondary" onClick={() => setEditOpen(true)}>
              {t("common.edit")}
            </button>
          )}
          {mayUpdate && (
            <button type="button" className="btn-secondary" onClick={() => setMergeOpen(true)}>
              {t("clients.merge.action")}
            </button>
          )}
          {mayDelete && (
            <button type="button" className="btn-secondary" onClick={() => setDeleting(true)}>
              {t("common.delete")}
            </button>
          )}
        </div>
      </div>

      {/* A prompt to look, not an action. */}
      {duplicates.length > 0 && mayUpdate && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning-line bg-warning-wash px-4 py-2 text-sm text-warning-text">
          <span>{t("clients.merge.possible", { count: duplicates.length })}</span>
          <button
            type="button"
            className="font-medium hover:underline"
            onClick={() => setMergeOpen(true)}
          >
            {t("clients.merge.review")}
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-4 flex gap-1 border-b border-line">
        {TABS.filter(tabVisible).map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => setTab(name)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${
              tab === name
                ? "border-accent text-accent-text"
                : "border-transparent text-ink-2 hover:text-ink"
            }`}
          >
            {t(`clients.tabs.${name}`)}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="card p-4">
              <p className="text-xs uppercase tracking-wide text-ink-2">
                {t("clients.stats.visits")}
              </p>
              <p className="text-lg font-semibold">{client.visits_count ?? 0}</p>
              <p className="text-xs text-ink-2">{t("clients.stats.visitsHint")}</p>
            </div>

            <div className="card p-4">
              <p className="text-xs uppercase tracking-wide text-ink-2">
                {t("clients.stats.lastVisit")}
              </p>
              <p className="text-lg font-semibold">
                {client.last_visit_at
                  ? formatDate(client.last_visit_at, i18n.language)
                  : t("clients.neverVisited")}
              </p>
              {client.first_visit_at && (
                <p className="text-xs text-ink-2">
                  {t("clients.stats.since", {
                    date: formatDate(client.first_visit_at, i18n.language),
                  })}
                </p>
              )}
            </div>

            <div className="card p-4">
              <p className="text-xs uppercase tracking-wide text-ink-2">
                {t("clients.stats.value")}
              </p>
              <p className="text-lg font-semibold">{money()}</p>
              {/* Booked, not collected — file 10 knows what was paid. */}
              <p className="text-xs text-ink-2">{t("clients.stats.basis.booked")}</p>
            </div>
          </div>

          <div className="card space-y-4 p-5">
            <div>
              <p className="field-label">{t("clients.form.language")}</p>
              <p className="text-sm text-ink">
                {client.preferred_language
                  ? t(`clients.languages.${client.preferred_language}`)
                  : t("clients.form.noLanguage")}
              </p>
              <p className="text-xs text-ink-2">{t("clients.form.languageHint")}</p>
            </div>

            <div>
              <p className="field-label">{t("clients.form.preferences")}</p>
              {Object.keys(client.preferences ?? {}).length === 0 ? (
                <p className="text-sm text-ink-2">{t("clients.noPreferences")}</p>
              ) : (
                <dl className="space-y-1">
                  {Object.entries(client.preferences).map(([key, value]) => (
                    <div key={key} className="flex gap-2 text-sm">
                      <dt className="w-40 shrink-0 text-ink-2">{key}</dt>
                      <dd className="text-ink">{value}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>

            <div>
              <p className="field-label">{t("clients.addedOn")}</p>
              <p className="text-sm text-ink">
                {formatDate(client.created_at, i18n.language)}
              </p>
            </div>
          </div>
        </div>
      )}

      {tab === "timeline" && <ClientTimeline clientId={client.id} />}
      {tab === "notes" && <ClientNotes clientId={client.id} />}
      {tab === "consent" && <ClientConsent clientId={client.id} />}
      {tab === "loyalty" && tabVisible("loyalty") && <LoyaltyPanel clientId={client.id} />}

      <ClientModal
        client={client}
        tags={tags}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSaved={(saved) => {
          setClient(saved);
          setToast({ type: "success", message: t("clients.updated", { name: saved.name }) });
        }}
      />

      <BookingModal
        open={bookingOpen}
        branches={branches}
        client={client}
        onClose={() => setBookingOpen(false)}
        onBooked={(appointment) => {
          setToast({
            type: "success",
            message: t("appointments.booked", { reference: appointment.reference }),
          });
          // The visit counters are maintained from the booking's own events, so
          // the record is reread rather than adjusted here.
          load();
        }}
      />

      <ClientMergeModal
        client={client}
        open={mergeOpen}
        onClose={() => setMergeOpen(false)}
        onMerged={(survivor, loser) => {
          setClient(survivor);
          setToast({
            type: "success",
            message: t("clients.merge.merged", { loser: loser.name, winner: survivor.name }),
          });
          load();
        }}
      />

      <ConfirmDialog
        open={deleting}
        title={t("clients.deleteTitle")}
        message={t("clients.deleteMessage", { name: client.name })}
        busy={deleteBusy}
        error={deleteError}
        onConfirm={confirmDelete}
        onClose={() => setDeleting(false)}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
}
